/**
 * @name MenuNotifications
 * @description Centro de notificaciones
 * @author Ricardo Guerra
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import i18n from 'i18next';
import moment from 'moment';
import styled from 'styled-components';

import { NotificationsService, SnackbarService } from '@services';
import { Icons, MUI, Typography } from '@styles';

import Notification from './notification';

const MenuNotifications = () => {
  const todayRef = useRef(moment());
  const [notifications, setNotifications] = useState([]);
  const [unReadNotifications, setUnReadNotifications] = useState(0);
  const [unSeenNotifications, setUnSeenNotifications] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);

  const toCounter = useCallback((total) => (total > 99 ? 99 : total), []);

  const translateProperties = useCallback((property, value) => {
    switch (property) {
      case 'mensaje':
        return value;
      // case 'periodo':
      case 'periodoFin':
        switch (value) {
          case 'Enero':
            return i18n.t('Months.jan');
          case 'Febrero':
            return i18n.t('Months.jan');
          case 'Marzo':
            return i18n.t('Months.mar');
          case 'Abril':
            return i18n.t('Months.apr');
          case 'Mayo':
            return i18n.t('Months.may');
          case 'Junio':
            return i18n.t('Months.jun');
          case 'Julio':
            return i18n.t('Months.jul');
          case 'Agosto':
            return i18n.t('Months.aug');
          case 'Septiembre':
            return i18n.t('Months.sep');
          case 'Octubre':
            return i18n.t('Months.oct');
          case 'Noviembre':
            return i18n.t('Months.nov');
          case 'Diciembre':
            return i18n.t('Months.dec');
          default:
            break;
        }
        break;
      case 'fecha':
      case 'fechaInicio':
      case 'fechaFin': {
        let months = ['Months.jan', 'Months.feb', 'Months.mar', 'Months.apr', 'Months.may', 'Months.jun',
          'Months.jul', 'Months.aug', 'Months.sep', 'Months.oct', 'Months.nov', 'Months.dec'];
        let pDate = value.split('/');
        switch (JSON.parse(localStorage.getItem('ls.ty-Language')).language) {
          case 'en':
            return `${i18n.t(months[parseInt(pDate[1]) - 1])} ${pDate[0]}, ${pDate[2]}`;
          case 'es':
          case 'pt':
            return `${pDate[0]} de ${i18n.t(months[parseInt(pDate[1]) - 1])} de ${pDate[2]}`;
          default:
            break;
        }
        break;
      }
      case 'tipoEvento':
        return (value === 1 ? i18n.t('GenericMessages.conversations') : i18n.t('GenericMessages.survey'));
      case 'tipoDelegado':
        return i18n.t(`GenericMessages.${value}`).toLowerCase();
      default:
        break;
    }
    return value;
  }, []);

  // Consulta las notificaciones del usuario
  const getNotifications = useCallback(() => {
    let notifications = [];
    const currentEmployee = JSON.parse(localStorage.getItem('ls.infoUsuario'))?.Empleado;
    if (!currentEmployee) {
      setNotifications([]);
      setUnSeenNotifications(0);
      setUnReadNotifications(0);
      return;
    }

    NotificationsService.get(currentEmployee.IdEmpleado)
      .then((response) => {
        notifications = response;
        notifications.forEach((notification) => {
          // Formato de tiempo de notificación
          let minutes = todayRef.current.diff(moment(notification.FechaTransaccion), 'minutes');
          let hours = todayRef.current.diff(moment(notification.FechaTransaccion), 'hours');
          let days = todayRef.current.diff(moment(notification.FechaTransaccion), 'days');

          if (days > 0) {
            notification.Tiempo = `${days} d`;
          } else {
            if (hours > 0) {
              notification.Tiempo = `${hours} h`;
            } else {
              notification.Tiempo = `${minutes} m`;
            }
          }

          let propertiesToTranslate = [/* 'periodo', */ 'periodoFin', 'fecha', 'fechaInicio', 'fechaFin', 'tipoEvento', 'tipoDelegado'];
          let message = i18n.t(`Notifications.${notification.MensajeNotificacion}`);

          // Traducción del mensaje de la notificación
          if (notification.Propiedades) {
            let properties = JSON.parse(notification.Propiedades);
            Object.keys(properties).forEach((property) => {
              if (propertiesToTranslate.includes(property)) {
                properties[property] = translateProperties(property, properties[property]);
              }
              message = message.replace(`<${property}>`, properties[property]);
            });

            // Mensajes de traducciones generales del administrador
            if (notification.Propiedades.includes('mensaje')) {
              message = properties['mensaje'];
            }
          }

          notification.MensajeNotificacion = message;
        });

        setUnSeenNotifications(toCounter(notifications.filter((notification) => !notification.Visto).length));
        setUnReadNotifications(toCounter(notifications.filter((notification) => !notification.Leido).length));
        setNotifications(notifications);
      })
      .catch((error) => {
        if (error && error.response && (error?.response.status !== 404 && error?.response.status !== 401)) {
          SnackbarService.error(i18n.t('Topbar.errorNotifications'));
        }
      });

    setNotifications(notifications);
  }, [toCounter, translateProperties]);


  // Update notifications in database as seen
  const markAllAsSeen = useCallback(() => {
    NotificationsService.put({ seen: true })
      .then(() => {
        setUnSeenNotifications(0);
      });
  }, []);

  // Update notifications in database as read
  const markAllAsRead = useCallback(() => {
    NotificationsService.put({ read: true })
      .then(() => {
        setNotifications((prevNotifications) => prevNotifications.map((notification) => ({ ...notification, Leido: true })));
        setUnReadNotifications(0);
      });
  }, []);

  /**
   * Read and redirect to notification state
   * @param {object} notification
   */
  const readNotification = useCallback((notification) => {
    NotificationsService.patch({ id: notification.Id, read: true })
      .then(() => {
        let nextNotifications = [];
        setNotifications((prevNotifications) => {
          nextNotifications = prevNotifications.map((item) => (item.Id === notification.Id ? { ...item, Leido: true } : item));
          return nextNotifications;
        });
        setUnReadNotifications(toCounter(nextNotifications.filter((item) => !item.Visto).length));

        if (notification.Parametros) {
          localStorage.setItem('ls.Parametros', notification.Parametros);
        }

        window.location.href = notification.URL;
      })
      .catch(() => {
        if (notification.Parametros) {
          localStorage.setItem('ls.Parametros', JSON.stringify(notification.Parametros));
        }

        window.location.href = notification.URL;
      });
  }, [toCounter]);

  /**
   * Delete notification
   * @param {number} id
   */
  const deleteNotification = useCallback((id) => {
    NotificationsService.delete(id)
      .then(() => {
        setNotifications((prevNotifications) => prevNotifications.filter((notification) => notification.Id !== id));
      });
  }, []);

  const openHandler = useCallback((event) => {
    setAnchorEl(event.currentTarget);
    if (unSeenNotifications > 0) {
      markAllAsSeen();
    }
  }, [markAllAsSeen, unSeenNotifications]);

  const closeHandler = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useEffect(() => {
    getNotifications();
  }, [getNotifications]);

  return (
    <>
      <Styled.BoxTopbarIcon onClick={openHandler}>
        <MUI.Tooltip title={i18n.t('Topbar.notifications')}>
          <Icons.Notifications />
        </MUI.Tooltip>
        {unSeenNotifications > 0 && (
          <Styled.NotificationWrapper>
            <Styled.NotificationNumber>
              {unSeenNotifications}
            </Styled.NotificationNumber>
          </Styled.NotificationWrapper>
        )}
      </Styled.BoxTopbarIcon>

      <Styled.Popover
        open={Boolean(anchorEl)}
        onClose={closeHandler}
        anchorEl={anchorEl}
      >
        <MUI.MenuHeader>
          <Styled.BoxHeader>
            <Styled.BoxHeaderContent>
              <Typography.Text>{i18n.t('Topbar.notificationsCenter')}</Typography.Text>
            </Styled.BoxHeaderContent>
            <Styled.BoxHeaderLink>
              {unReadNotifications > 0 && (
                <MUI.ButtonLink onClick={markAllAsRead}>{i18n.t('Topbar.markAllAsRead')}</MUI.ButtonLink>
              )}
            </Styled.BoxHeaderLink>
          </Styled.BoxHeader>
        </MUI.MenuHeader>

        <Styled.BoxNotifications>
          {notifications.map((notification) => (
            <Styled.MenuItem key={notification.Id} read={notification.Leido.toString()}>
              <Notification
                notification={notification}
                onReadNotification={readNotification}
                onDeleteNotification={deleteNotification}
              />
            </Styled.MenuItem>
          ))}

          {notifications.length === 0 && (
            <Styled.MenuNoItems>
              <Typography.Text>{i18n.t('Topbar.noNotifications')}</Typography.Text>
            </Styled.MenuNoItems>
          )}
        </Styled.BoxNotifications>
      </Styled.Popover>
    </>
  );
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.BoxTopbarIcon = styled(MUI.ButtonText)`
  &.MuiButton-root{
    display: flex;
    min-width: 60px;
    width: 60px;
    height: 60px;
    padding: 12px;
    margin: 0;

    &:hover {
      text-decoration: none;
      background-color: ${(props) => props.theme.colors.greyHover};
      border-radius: 0%;
    }
  }
`;

Styled.NotificationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  position: absolute;
  top: 12px;
  right: 12px;
  color: ${(props) => props.theme.colors.white};
  background-color: ${(props) => props.theme.colors.red};
`;

Styled.NotificationNumber = styled.span`
  position: absolute;
  font-size: 12px;
  font-weight: 700;
`;

Styled.BoxHeader = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  width: 100%;
`;

Styled.BoxHeaderContent = styled.div`
  display: flex;
  width: 100%;
`;

Styled.BoxHeaderLink = styled(Styled.BoxHeaderContent)`
  display: flex;
  width: 100%;
  justify-content: flex-end;
`;

Styled.BoxNotifications = styled.div`
  max-height: 470px;
  overflow-y: auto;
  background: ${(props) => props.theme.colors.white};
  scrollbar-width: thin !important; /* Scrollbar Firefox */
`;

// *************************************
// GLOBAL STYLED COMPONENTS           
// *************************************

Styled.Popover = styled(MUI.Popover)`
  .MuiPopover-paper {
    max-height: 520px;
    max-width: 450px;
    width: 450px;
    left: auto !important;
    right: 16px !important;
    top: 60px !important;
    overflow-y: hidden;
    overflow-x: hidden;
  }
`;

Styled.MenuItem = styled(MUI.MenuItem)`
  &.MuiMenuItem-root {
    height: initial;
    min-height: 78px;
    padding: 12px 8px;
    border-bottom: 1px solid ${(props) => props.theme.colors.greyHover};
    background-color: ${(props) => props.read === 'true' ? (props) => props.theme.colors.white : '#EDF8FF'};
    &:hover {
      background-color: ${(props) => props.theme.colors.greyHover};
    }
    &:last-child {
      border-bottom: none;
    }
  }
`;

Styled.MenuNoItems = styled(MUI.MenuItem)`
  &.MuiMenuItem-root {
    height: initial;
    padding: 16px;
    background-color: ${(props) => props.theme.colors.white};
    &:hover {
      background-color: ${(props) => props.theme.colors.white};
      cursor: auto;
    }
  }
`;

export default MenuNotifications;
